import { useCallback, useRef } from 'react'

import type { ChatDashboardConfig } from '@factorialco/f0-react'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ChartResult = {
  categories?: string[]
  indicators?: Array<{ name: string; max?: number }>
  series?:
    | Array<{ name: string; data: number[] }>
    | { name: string; data: Array<{ name: string; value: number }> }
    | { value: number; name?: string }
  xCategories?: string[]
  yCategories?: string[]
  data?: [number, number, number][]
  min?: number
  max?: number
}

type MetricResult = {
  value: number
}

type CollectionResult = {
  rows: Record<string, unknown>[]
  total: number
}

export type ItemResult = {
  chart?: ChartResult
  metric?: MetricResult
  collection?: CollectionResult
  error?: string
}

type ComputeResponse = {
  results: Record<string, ItemResult>
  filterOptions?: Record<string, string[]>
}

type ApiConfig = {
  baseUrl: string
  headers: Record<string, string>
  runtimeFetch?: typeof fetch
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Provides a `fetchItem` function that triggers a batch compute request
 * and returns the result for a specific item.
 *
 * Concurrent calls with the same filter state share one request; when
 * filters change a new request is made and the previous one aborted.
 */
export function useDashboardCompute(
  config: ChatDashboardConfig,
  apiConfig: ApiConfig,
  refreshKey: number
): {
  fetchItem: (
    itemId: string,
    filterValues: Record<string, unknown[]>,
    navigationFilterValues?: Record<string, string[]>
  ) => Promise<ItemResult>
  getFilterOptions: () => Record<string, string[]> | undefined
} {
  const inflightRef = useRef<{
    key: string
    promise: Promise<ComputeResponse>
    controller: AbortController
  } | null>(null)

  const lastResponseRef = useRef<ComputeResponse | undefined>()

  const configRef = useRef(config)
  configRef.current = config
  const apiConfigRef = useRef(apiConfig)
  apiConfigRef.current = apiConfig
  const refreshKeyRef = useRef(refreshKey)
  refreshKeyRef.current = refreshKey

  const fetchItem = useCallback(
    (
      itemId: string,
      filterValues: Record<string, unknown[]>,
      navigationFilterValues?: Record<string, string[]>
    ): Promise<ItemResult> => {
      const requestKey = `${refreshKeyRef.current}:${JSON.stringify(filterValues)}:${JSON.stringify(navigationFilterValues ?? {})}`

      if (inflightRef.current?.key === requestKey) {
        return inflightRef.current.promise.then(
          (res) => res.results[itemId] ?? { error: 'No result for item' }
        )
      }

      inflightRef.current?.controller.abort()

      const controller = new AbortController()
      const cfg = configRef.current
      const api = apiConfigRef.current
      const runtimeFetch = api.runtimeFetch ?? fetch

      const stringFilterValues: Record<string, string[]> = {}
      for (const [key, vals] of Object.entries(filterValues)) {
        if (Array.isArray(vals) && vals.length > 0) {
          stringFilterValues[key] = vals.map(String)
        }
      }

      const hasNavValues = navigationFilterValues && Object.keys(navigationFilterValues).length > 0

      // Derive `dateNavigatorColumn` from the spec so the backend can re-derive
      // the matching `nf_${column}` navigation filter and apply the date range
      // WHERE clause. The backend filter pipeline keys nav filters by
      // `nf_${column}`, so the FE-side filter values (keyed by the spec's
      // `navigationFilters` map keys) must align with that — see
      // `factorial-agent/src/mastra/one/derive-dashboard-filters.ts`.
      const navSpecs = cfg.navigationFilters ?? {}
      let dateNavigatorColumn: string | undefined
      for (const spec of Object.values(navSpecs)) {
        if (spec.type === 'dateNavigation') {
          dateNavigatorColumn = spec.column
          break
        }
      }

      const body = {
        fetchSpecs: cfg.fetchSpecs,
        items: cfg.items,
        filters: cfg.filters,
        filterValues: Object.keys(stringFilterValues).length > 0 ? stringFilterValues : undefined,
        navigationFilters: cfg.navigationFilters,
        navigationFilterValues: hasNavValues ? navigationFilterValues : undefined,
        ...(dateNavigatorColumn ? { dateNavigatorColumn } : {}),
      }

      const promise = runtimeFetch(`${api.baseUrl}/dashboard/compute`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...api.headers,
        },
        body: JSON.stringify(body),
        signal: controller.signal,
      }).then(async (res) => {
        if (!res.ok) {
          const text = await res.text().catch(() => '')
          throw new Error(`Dashboard compute failed: ${res.status} ${text}`)
        }
        // eslint-disable-next-line no-type-assertion/no-type-assertion -- response shape declared by backend
        const data = (await res.json()) as ComputeResponse
        lastResponseRef.current = data
        return data
      })

      inflightRef.current = { key: requestKey, promise, controller }

      return promise.then((res) => res.results[itemId] ?? { error: 'No result for item' })
    },
    []
  )

  const getFilterOptions = useCallback(() => lastResponseRef.current?.filterOptions, [])

  return { fetchItem, getFilterOptions }
}
